import React from "react";
import axios from "axios";
import { csvToRows } from "./csv";

/** Keep in sync with server/utils/bulkRowsUpload.js */
export const MAX_BULK_UPLOAD_BYTES = 10 * 1024 * 1024; // 10 MB

export const FILE_TOO_LARGE_MESSAGE =
  "This file is too large to upload. Please use a CSV under 10 MB.";

/**
 * Normalize bulk-upload failures into a user-facing toast message.
 */
export function getBulkUploadErrorMessage(err) {
  const status = err?.response?.status;
  const apiMessage = err?.response?.data?.message;
  const rawMessage = String(apiMessage || err?.message || "");

  if (
    status === 413 ||
    /too large|LIMIT_FILE_SIZE|entity too large|max(?:imum)? size/i.test(
      rawMessage,
    )
  ) {
    return FILE_TOO_LARGE_MESSAGE;
  }

  if (apiMessage) return apiMessage;
  if (err?.message) return err.message;
  return "Failed to upload CSV data.";
}

/**
 * Parse a CSV file locally, then upload rows as a multipart JSON file
 * (avoids express.json body size limits). Server business logic unchanged.
 * @returns {Promise<object>} response.data from the API
 */
export function uploadCsvBulk({ file, endpoint }) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("Please select a file to upload."));
      return;
    }

    if (typeof file.size === "number" && file.size > MAX_BULK_UPLOAD_BYTES) {
      reject(new Error(FILE_TOO_LARGE_MESSAGE));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read the selected file."));
    reader.onload = async (e) => {
      try {
        const rows = csvToRows(String(e.target.result ?? ""));
        if (!rows.length) {
          reject(new Error("No data rows found in the CSV file."));
          return;
        }

        const payload = JSON.stringify(rows);
        if (payload.length > MAX_BULK_UPLOAD_BYTES) {
          reject(new Error(FILE_TOO_LARGE_MESSAGE));
          return;
        }

        const formData = new FormData();
        formData.append(
          "file",
          new Blob([payload], { type: "application/json" }),
          "rows.json",
        );

        const response = await axios.post(endpoint, formData);
        resolve(response.data);
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsText(file);
  });
}

/** Shared bulk-upload toast (+ skipped). */
export function showBulkUploadSummaryToast(notificationCtx, summary = {}) {
  const { inserted, updated, skipped, failed, errors, skips } = summary;
  const allFailed =
    (failed ?? 0) > 0 &&
    (inserted ?? 0) === 0 &&
    (updated ?? 0) === 0 &&
    (skipped ?? 0) === 0;

  notificationCtx.show({
    message: (
      <>
        <div style={{ fontWeight: "600", marginBottom: "8px" }}>
          <strong>
            {allFailed ? "Bulk Upload Failed" : "Bulk Upload Completed"}
          </strong>
        </div>
        <div
          style={{
            lineHeight: "1.5",
            fontSize: "14px",
            textAlign: "left",
          }}
        >
          <div style={{ marginBottom: "10px" }}>
            <div>
              Inserted: <strong>{inserted ?? 0}</strong>
            </div>
            <div>
              Updated: <strong>{updated ?? 0}</strong>
            </div>
            <div>
              Skipped: <strong>{skipped ?? 0}</strong>
            </div>
            <div>
              Failed: <strong>{failed ?? 0}</strong>
            </div>
          </div>

          {skips && Object.keys(skips).length > 0 && (
            <div style={{ marginBottom: "10px" }}>
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Skip Breakdown:
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px" }}>
                {Object.entries(skips).map(([msg, count], idx) => (
                  <li key={`skip-${idx}`}>
                    {msg} <strong>({count})</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {errors && Object.keys(errors).length > 0 && (
            <div>
              <div style={{ fontWeight: "500", marginBottom: "4px" }}>
                Error Breakdown:
              </div>
              <ul style={{ margin: 0, paddingLeft: "18px" }}>
                {Object.entries(errors).map(([msg, count], idx) => (
                  <li key={`err-${idx}`}>
                    {msg} <strong>({count})</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </>
    ),
    stat: allFailed ? "error" : "success",
  });
}
